import { useCallback } from 'react';
import { useAzureAuth } from './useAzureAuth';
import { useAuthState, useAuthDispatch } from '../contexts/auth-context';
import { logout as logoutAction } from '../contexts/actions/authActions';

/**
 * Enhanced auth hook that combines Azure Auth with the existing auth system
 * This provides a unified interface for authentication across the app
 */
export const useAuth = () => {
  const {
    authState: azureAuthState,
    login: azureLogin,
    logout: azureLogout
  } = useAzureAuth();
  const { auth: legacyAuthState } = useAuthState();
  const dispatch = useAuthDispatch();

  // Combined authentication state
  const isAuthenticated =
    legacyAuthState.isAuthenticated || azureAuthState.isAuthenticated;
  const isLoading = azureAuthState.isLoading || legacyAuthState.isLogin;
  const user = legacyAuthState.user || azureAuthState.user;
  const error = azureAuthState.error ?? legacyAuthState.error ?? null;

  // Enhanced login function
  const login = useCallback(async (): Promise<void> => {
    try {
      // Store the current path for redirect after login
      const currentPath = window.location.pathname;
      if (currentPath !== '/login') {
        sessionStorage.setItem('auth_redirect_path', currentPath);
      }

      await azureLogin();
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  }, [azureLogin]);

  // Enhanced logout function
  const logout = useCallback(async (): Promise<void> => {
    try {
      // Clear Azure auth state
      await azureLogout();

      // Clear legacy auth state
      await logoutAction()(dispatch);
    } catch (error) {
      console.error('Logout failed:', error);
      throw error;
    }
  }, [azureLogout, dispatch]);

  return {
    isAuthenticated,
    isLoading,
    user,
    error,
    login,
    logout
    // Expose individual auth states for debugging
    // azureAuthState,
    // legacyAuthState
  };
};

export default useAuth;
